import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth, SSOResponseData, SSOUser } from '@freelancer/auth';
import { USE_FAKES } from '@freelancer/config';
import { TotpMethod } from '@freelancer/datastore/collections';
import { FacebookAuthResponse } from '@freelancer/facebook';
import { ResponseData } from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import { Interface } from '@freelancer/types';
import { AppleSignInResponse } from '@ionic-native/sign-in-with-apple/ngx';
import { RoleApi } from 'api-typings/common/common';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { GafExceptionCodesApi } from 'api-typings/gaf/gaf';
import * as Rx from 'rxjs';
import {
  AuthSuccessResponseAjax,
  LoginError,
  UserCheckError,
} from '../login-signup.backend-model';
import {
  AuthResponse,
  SSOActionResponse,
  TwoFactorResponse,
} from '../login-signup.model';
import { LoginSignupService } from '../login-signup.service';

interface RedirectParams {
  redirectDisabled?: boolean;
  isModal?: boolean;
}

type SavedState =
  | {
      user: string;
      password: string;
    }
  | {
      ssoAuthPromise: Promise<
        ResponseData<any, string> & {
          provider: 'apple' | 'facebook';
        }
      >;
      ssoDetailsPromise: Promise<SSOUser | undefined>;
      /**
       * The original response from the appropriate FLN login endpoint
       * Should always be an `SSOActionResponse` but is typed as AuthResponse
       * to reduce type messiness in the usages
       */
      ssoActionPromise: Promise<ResponseData<AuthResponse, LoginError>>;
    };

export enum LoginTestingUsernames {
  basicEmployer = 1, // Don't start at 0
  basicFreelancer,
  experiencedEmployer,
  experiencedFreelancer,
  // Fixed user ID so that we can have a constant avatar for app store screenshots.
  appStoreScreenshotsUser = 5,
}

const usernames: { [key: string]: number } = {
  basicEmployer: LoginTestingUsernames.basicEmployer,
  basicFreelancer: LoginTestingUsernames.basicFreelancer,
  experiencedEmployer: LoginTestingUsernames.experiencedEmployer,
  experiencedFreelancer: LoginTestingUsernames.experiencedFreelancer,
  appStoreScreenshotsUser: LoginTestingUsernames.appStoreScreenshotsUser,
};

/**
 * Service which provides an interface to backend endpoints for the login/signup funnel
 */
@Injectable({
  providedIn: 'root',
})
export class LoginSignupTesting implements Interface<LoginSignupService> {
  static apiFailingEmail = 'takenemail@gmail.com';
  static apiFailingPassword = 'InvalidUIPW1!';
  private savedState: SavedState = {
    user: '',
    password: '',
  };

  constructor(
    private auth: Auth,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    @Inject(USE_FAKES) private useFakes: boolean,
  ) {
    if (this.useFakes) {
      console.log(
        `Welcome to the in-memory app. You can login using one of the following username (password is "password"): `,
        Object.keys(usernames),
      );
    }
  }

  login(
    user: string,
    password: string,
  ): Promise<ResponseData<AuthResponse, LoginError>> {
    return Promise.resolve(
      password === 'password'
        ? ({
            status: 'success',
            result: {
              user: user in usernames ? usernames[user] : 123,
              token: 'token',
            },
          } as const)
        : ({
            status: 'error',
            errorCode: ErrorCodeApi.INTERNAL_SERVER_ERROR,
          } as const),
    );
  }

  verify(
    preLoginToken: string,
    password: string,
  ): Promise<ResponseData<AuthSuccessResponseAjax, LoginError>> {
    return Promise.resolve(
      password === 'password'
        ? ({
            status: 'success',
            result: { user: 123, token: 'token' },
          } as const)
        : ({
            status: 'error',
            errorCode: ErrorCodeApi.INTERNAL_SERVER_ERROR,
          } as const),
    );
  }

  resend2FACode(
    preLoginToken: string,
    method?: TotpMethod,
  ): Promise<ResponseData<TwoFactorResponse, LoginError>> {
    return Promise.resolve({
      status: 'success',
      result: { preLoginToken: 'token' },
    } as const);
  }

  facebookLogin(
    authResponse: FacebookAuthResponse,
  ): Promise<ResponseData<AuthSuccessResponseAjax, LoginError>> {
    return Promise.resolve({
      status: 'success',
      result: { user: 123, token: 'token' },
    } as const);
  }

  facebookLinkLogin(
    email: string,
    password: string,
    authResponse: FacebookAuthResponse,
  ): Promise<ResponseData<AuthSuccessResponseAjax, LoginError>> {
    return Promise.resolve(
      password === 'password'
        ? ({
            status: 'success',
            result: { user: 123, token: 'token' },
          } as const)
        : ({
            status: 'error',
            errorCode: ErrorCodeApi.INTERNAL_SERVER_ERROR,
          } as const),
    );
  }

  checkUserDetails(
    user: {
      username?: string;
      email?: string;
    },
    password?: string,
  ): Promise<ResponseData<undefined, UserCheckError>> {
    return Promise.resolve(
      password !== LoginSignupTesting.apiFailingPassword &&
        user.email !== LoginSignupTesting.apiFailingEmail
        ? ({
            status: 'success',
            result: undefined,
          } as const)
        : ({
            status: 'error',
            errorCode: ErrorCodeApi.INTERNAL_SERVER_ERROR,
          } as const),
    );
  }

  signup({
    email,
    username,
    role,
    password,
    privacyConsent,
    personalUse,
    facebookAuth,
    captcha,
  }: {
    email: string;
    username: string;
    role: RoleApi;
    password: string;
    privacyConsent: boolean;
    personalUse?: boolean;
    facebookAuth?: FacebookAuthResponse;
    captcha?: {
      challenge: string;
      response: string;
    };
  }): Promise<ResponseData<AuthSuccessResponseAjax, LoginError>> {
    return Promise.resolve(
      password === 'password'
        ? ({
            status: 'success',
            result: { user: 123, token: 'token' },
          } as const)
        : ({
            status: 'error',
            errorCode: ErrorCodeApi.INTERNAL_SERVER_ERROR,
          } as const),
    );
  }

  /**
   * Handle successful authentication, be it on login or signup
   */
  handleSuccess(
    action: 'login' | 'signup',
    userId: number,
    authToken: string,
    rememberLogin: boolean,
    redirectParams?: RedirectParams,
  ): Promise<boolean | undefined> {
    // set auth session (automatically sets cookies)
    this.auth.setSession(userId.toString(), authToken);

    return this.getRedirectUrl(action, redirectParams).then(redirectUrl => {
      if (!redirectUrl) {
        return;
      }

      if (redirectUrl.startsWith('http')) {
        // absolute URL: do a hard navigate
        return this.location.redirect(redirectUrl);
      }
      // parse query params and fragments in the nextUrl
      const urlTree = this.router.parseUrl(redirectUrl);
      return this.location.navigateByUrl(urlTree);
    });
  }

  /**
   * Logs a user in on other domains that they should be logged in on.
   * Fetches domains from `getCrossDomainSSODomains.php`, then sends requests
   * to those domains to set cookies on them.
   */
  setCrossDomainSSO(
    userId: number,
    authToken: string,
    rememberLogin: boolean,
  ): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Returns the next route to load after logging in or signing up
   *
   * Note that this is only called on successful logins and other redirections
   * may still occur for failed attempts.
   *
   * Eg. Suspended users may be redirected to the suspended account page.
   */
  getRedirectUrl(
    action: 'login' | 'signup',
    { isModal = false, redirectDisabled = false }: RedirectParams = {},
  ): Promise<string> {
    const { next, goto } = this.activatedRoute.snapshot.queryParams;
    console.log(`Ignoring these: next '${next}', goto '${goto}'.`);

    const defaultUrl = 'dashboard'; // FIXME: Should this use next or goto?
    return Promise.resolve(defaultUrl);
  }

  resetPassword(
    email: string,
  ): Promise<ResponseData<undefined, 'UNKNOWN_ERROR'>> {
    return Promise.resolve({
      status: 'success',
      result: undefined,
    } as const);
  }

  changePassword({
    currentPassword,
    newPassword,
  }: {
    currentPassword: string;
    newPassword: string;
  }): Promise<ResponseData<undefined, 'UNKNOWN_ERROR'>> {
    return Promise.resolve({
      status: 'success',
      result: undefined,
    } as const);
  }

  closeAccount({
    reason,
    canContact,
    feedback,
  }: {
    reason: string;
    canContact: boolean;
    feedback?: string;
  }): Promise<ResponseData<undefined, GafExceptionCodesApi.UNKNOWN_ERROR>> {
    return Rx.of({
      status: 'success',
      result: undefined,
    } as const).toPromise();
  }

  /**
   * Saves state that needs to be shared across login / signup when switching routes
   */
  saveState(state: LoginSignupService['savedState']): void {
    this.savedState = state;
  }

  /**
   * Returns and clears the saved state.
   */
  getSavedState() {
    const state = this.savedState;
    this.savedState = {
      user: '',
      password: '',
    };
    return state;
  }

  appleLogin(
    authResponse: AppleSignInResponse,
  ): Promise<
    ResponseData<AuthSuccessResponseAjax | SSOActionResponse, LoginError>
  > {
    throw new Error('UNIMPLEMENTED TEST METHOD');
  }

  appleLoginLink(
    email: string,
    password: string,
    appleIdentityToken: string,
  ): Promise<ResponseData<AuthSuccessResponseAjax, LoginError>> {
    throw new Error('UNIMPLEMENTED TEST METHOD');
  }

  /**
   * Triggers an SSO login flow.
   *
   * Calls the appropriate SDK to authenticate the user,
   * then fetches user details and sends the data to our auth backend.
   */
  triggerSSOLogin(
    provider: 'facebook' | 'apple',
  ): {
    /**
     * Promise that resolves when the user has submitted or cancelled their login,
     * returning either a successful auth response or an error
     */
    authPromise: Promise<
      | SSOResponseData<'facebook', FacebookAuthResponse>
      | SSOResponseData<'apple', AppleSignInResponse>
    >;
    /**
     * Promise that fetches a user's details through the SSO provider
     * Errors if the initial authPromise errored
     */
    detailsPromise: Promise<SSOUser | undefined>;
    /**
     * Promise that logs the user in via the FLN auth backend
     * Errors if the initial authPromise errored
     */
    loginPromise: Promise<
      ResponseData<AuthSuccessResponseAjax | SSOActionResponse, LoginError>
    >;
  } {
    throw new Error('UNIMPLEMENTED TEST METHOD');
  }
}
